import { filterHistory, groupHistory, type HistoryEntry } from '../../domain/history';
import { songQuery } from '../../domain/song-search';
import { findStation } from '../../domain/stations';
import { type Lang, translate } from '../../i18n';
import { clear, el, icon } from '../../lib/dom';
import type { AppState } from '../../store/app';
import { createSearchField } from '../components/search-field';
import { createSongMenu } from '../components/song-menu';
import { formatClock, formatHistoryStamp, formatTrack } from '../format';

/**
 * Zaslon Zgodovina (P2 §4) — odigrane skladbe, združene po postaji.
 *
 * Skupine imajo **lepljivo glavo** (`.listgroup__header`, DS) z imenom postaje
 * in časom poslušanja `HH:MM:SS` (`.tnum`, sicer cifre poskakujejo). Vrstica
 * skladbe je gumb: tap **kopira naslov** (§4.4), gumb pa hkrati prepreči
 * izbiro besedila ob dolgem pritisku, ki bi jo `<div>` dovolil.
 *
 * **Dolg seznam brez zatikanja** je `content-visibility: auto` na vrstici
 * (§4.9, app.css), ne JS virtualizacija — ta pokvari Ctrl+F in bralnik zaslona.
 *
 * **Živa ura tekoče seje** (§4.6): čas postaje, ki igra, teče. Zaslon ima za
 * to lasten interval, dejaven le, kadar je zavihek Zgodovina viden in nekaj
 * igra — ne globalen utrip, ki bi izrisoval vse zaslone vsako sekundo.
 */

export interface HistoryScreenDeps {
  /** Skupni čas postaje, z odsekom v teku (živa ura); iz dnevnika. */
  listenMs(stationId: string, now: number): number;
  /** Kopiraj besedilo skladbe na odložišče (§4.4). */
  onCopyTitle(text: string): void;
  onClearAll(): void;
  onClearStation(stationId: string): void;
}

interface GroupNode {
  root: HTMLElement;
  name: HTMLSpanElement;
  duration: HTMLSpanElement;
  clearBtn: HTMLButtonElement;
}

export function createHistoryScreen(
  root: HTMLElement,
  deps: HistoryScreenDeps,
): { update(state: AppState): void } {
  let query = '';
  let lastState: AppState | null = null;
  let renderedKey = '';
  let ticker: ReturnType<typeof setInterval> | null = null;
  const groups = new Map<string, GroupNode>();

  /* ── Orodna vrstica: iskanje + počisti vse ───────────────────────── */
  const search = createSearchField('historySearch', (value: string) => {
    query = value;
    paint();
  });

  const clearAll = el('button', { class: 'btn secondary nw-history__clear', type: 'button' });
  clearAll.addEventListener('click', deps.onClearAll);

  const toolbar = el('div', { class: 'nw-toolbar' }, [search.root, clearAll]);

  const listRoot = el('div', { class: 'nw-history' });

  const emptyTitle = el('div', { class: 'ftitle' });
  const emptyBody = el('div', { class: 'desc' });
  const empty = el('div', { class: 'empty', hidden: '' }, [emptyTitle, emptyBody]);

  clear(root);
  root.append(toolbar, listRoot, empty);

  function stationName(state: AppState, stationId: string): string {
    return (
      findStation(state.stations, stationId)?.name ??
      translate(state.lang, 'history.unknownStation')
    );
  }

  function copyText(lang: Lang, entry: HistoryEntry): string {
    return formatTrack(lang, {
      artist: entry.artist ?? null,
      title: entry.title,
      raw: entry.raw,
    });
  }

  function buildEntry(state: AppState, entry: HistoryEntry): HTMLElement {
    const lang = state.lang;
    const text = copyText(lang, entry);

    /* Ura sama je bila dvoumna: vnosa iste postaje ob 14:32 danes in prejšnji
       teden sta bila enaka, skupine pa so po postaji in ne po dnevu (§4.13). */
    const time = el('span', { class: 'nw-history__at tnum' });
    time.textContent = formatHistoryStamp(lang, entry.at, Date.now());

    /**
     * ⚠️ **Vrstica ni več `<button>`, ampak ovoj z gumbom v sebi.**
     *
     * Odkar ima poleg kopiranja še meni »poišči skladbo« (P2 §4.8), bi bila
     * gumb v gumbu — neveljaven HTML, ki ga DS izrecno prepoveduje
     * (`components.css` §19: *„`.ao-row` NE sme biti `<button>`, kadar vsebuje
     * gumbe"*). Klikabilen je `.ao-row__main`, kar je isti markup kot na
     * Postajah.
     *
     * Gumb (in ne `<div>`) ostane zato, ker dolg pritisk nanj **ne izbere
     * besedila** — kar je pri vrstici, ki ob tapu kopira, edina razlika med
     * »kopirano« in »označeno« (§4.4).
     */
    const main = el('button', { class: 'ao-row__main nw-history__main', type: 'button' }, [
      el('span', { class: 'nw-history__song' }, [text]),
      time,
    ]);
    main.setAttribute('aria-label', translate(lang, 'history.copy', { track: text }));
    main.addEventListener('click', () => {
      deps.onCopyTitle(text);
    });

    /**
     * »Poišči skladbo« (P2 §4.8) — isti meni kot v medijski vrstici.
     *
     * Tu je dosegljiv **na vsaki širini**, tudi na telefonu, kjer ga vrstica
     * nima (glej `nowplaying.ts`). Skladba, ki igra zdaj, je v tem seznamu prvi
     * vnos, zato ta pot pokrije oboje.
     */
    const menu = createSongMenu('ao-row__act', 18);
    menu.update(
      lang,
      songQuery({ artist: entry.artist ?? null, title: entry.title, raw: entry.raw }),
    );

    return el('div', { class: 'ao-row nw-history__row' }, [
      main,
      el('span', { class: 'ao-row__acts' }, [menu.root]),
    ]);
  }

  function buildGroup(state: AppState, stationId: string, entries: HistoryEntry[]): GroupNode {
    const name = el('span', { class: 'nw-history__name' });
    const duration = el('span', { class: 'nw-history__time tnum' });
    const clearBtn = el('button', {
      class: 'nw-history__group-clear icon-trigger ghost',
      type: 'button',
    });
    clearBtn.append(icon('i-trash', 16));
    clearBtn.addEventListener('click', () => {
      deps.onClearStation(stationId);
    });

    const header = el('div', { class: 'listgroup__header nw-history__header' }, [
      name,
      duration,
      clearBtn,
    ]);
    const list = el(
      'div',
      { class: 'ao-list nw-history__list', role: 'group' },
      entries.map((entry) => buildEntry(state, entry)),
    );

    const groupRoot = el('section', { class: 'nw-history__group' }, [header, list]);
    return { root: groupRoot, name, duration, clearBtn };
  }

  function rebuild(state: AppState, grouped: ReturnType<typeof groupHistory>): void {
    groups.clear();
    const nodes: HTMLElement[] = [];
    for (const group of grouped) {
      const node = buildGroup(state, group.stationId, group.entries);
      groups.set(group.stationId, node);
      nodes.push(node.root);
    }
    listRoot.replaceChildren(...nodes);
  }

  /** Osveži imena, čase in oznake — brez ponovne gradnje vrstic. */
  function refresh(state: AppState): void {
    const lang = state.lang;
    const at = Date.now();
    for (const [stationId, node] of groups) {
      node.name.textContent = stationName(state, stationId);
      node.duration.textContent = formatClock(deps.listenMs(stationId, at));
      node.clearBtn.setAttribute(
        'aria-label',
        translate(lang, 'history.clearStation', { station: node.name.textContent }),
      );
    }
  }

  /** Posodobi samo čas postaje, ki igra (živa ura, §4.6). */
  function tickLive(): void {
    if (lastState === null) return;
    if (lastState.playback !== 'playing' || lastState.stationId === null) return;
    const node = groups.get(lastState.stationId);
    if (node === undefined) return;
    node.duration.textContent = formatClock(deps.listenMs(lastState.stationId, Date.now()));
  }

  function manageTicker(state: AppState): void {
    const live = state.tab === 'history' && state.playback === 'playing';
    if (live && ticker === null) ticker = setInterval(tickLive, 1000);
    else if (!live && ticker !== null) {
      clearInterval(ticker);
      ticker = null;
    }
  }

  function paint(): void {
    const state = lastState;
    if (state === null) return;
    const lang = state.lang;

    search.update(lang, 'history.search.label', 'history.search.placeholder');
    search.setValue(query);

    const entries = filterHistory(state.history, query);
    const grouped = groupHistory(entries);

    // Ponovna gradnja le, kadar se sestava spremeni — sicer bi vsak tik izgubil
    // fokus in kopiranje bi ob sočasni skladbi prezidalo vrstico pod prstom.
    const key = `${query} ${entries.map((e) => e.id).join(',')}`;
    if (key !== renderedKey) {
      renderedKey = key;
      rebuild(state, grouped);
    }
    refresh(state);

    // Prazno stanje: prazno zaradi iskanja je nekaj drugega kot prazna
    // zgodovina — pot ven je drugačna (P4 §6).
    const hasHistory = state.history.length > 0;
    const showEmpty = entries.length === 0;
    empty.toggleAttribute('hidden', !showEmpty);
    listRoot.toggleAttribute('hidden', showEmpty);
    clearAll.disabled = !hasHistory;
    clearAll.textContent = translate(lang, 'history.clearAll');

    const filtered = hasHistory && query.trim() !== '';
    emptyTitle.textContent = translate(
      lang,
      filtered ? 'history.empty.filtered.title' : 'history.empty.title',
    );
    emptyBody.textContent = translate(
      lang,
      filtered ? 'history.empty.filtered.body' : 'history.empty.body',
    );

    manageTicker(state);
  }

  return {
    update(state) {
      lastState = state;
      paint();
    },
  };
}
